/**
 * Staff "Family Portal" chart tab (Phase 12, plan §4.2).
 *
 * Lists the patient's legal guardians (patient_guardians) with their invite state and the
 * portal users with their status. Actions: Invite, Resend / Cancel, Approve (relationship +
 * legal authority), Revoke (reason), Age-18 review. The server enforces every rule; buttons
 * mirror its canInvite / status answers and the caller's permissions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, HeartHandshake, AlertTriangle, Info } from 'lucide-react';
import FamilyPortalService, { CANNOT_INVITE_TEXT, familyPortalErrorMessage } from '../../../services/admin/familyPortal.service';
import type {
  FamilyPortalGuardianRow,
  FamilyPortalInvite,
  FamilyPortalState,
  FamilyPortalUser,
  LegalAuthorityCode,
} from '../../../dtos/portal/familyPortalAdmin.dto';
import { useHasAnyPermission } from '../../../hooks/usePermission';
import type { PermissionString } from '../../../types/permissions';
import { useToast } from '../../common/Toast';
import ReasonDialog from '../../common/ReasonDialog';
import ConfirmDialog from '../../common/ConfirmDialog';
import InviteDialog from './InviteDialog';
import ApproveDialog from './ApproveDialog';
import Age18ReviewDialog from './Age18ReviewDialog';
import {
  INVITE_STATUS_LABELS,
  PORTAL_USER_STATUS_LABELS,
  STATUS_CLASSES,
  legalAuthorityLabel,
} from './familyPortalText';

export const FAMILY_PORTAL_READ = 'admin:patient:familyportal:read' as PermissionString;
export const FAMILY_PORTAL_WRITE = 'admin:patient:familyportal:write' as PermissionString;
export const FAMILY_PORTAL_APPROVE = 'admin:patient:familyportal:approve' as PermissionString;

interface FamilyPortalTabProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
}

const Pill: React.FC<{ status: string; label: string }> = ({ status, label }) => (
  <span className={`inline-block rounded border px-2 py-0.5 text-xs font-semibold ${STATUS_CLASSES[status] ?? STATUS_CLASSES.ENDED}`}>
    {label}
  </span>
);

const smallButton =
  'rounded-md px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500';
const primarySmall = `${smallButton} bg-slate-800 text-white hover:bg-slate-700`;
const secondarySmall = `${smallButton} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
const dangerSmall = `${smallButton} border border-red-300 bg-white text-red-700 hover:bg-red-50`;

const fmtDate = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

type DialogState =
  | { kind: 'none' }
  | { kind: 'invite'; guardian: FamilyPortalGuardianRow; mode: 'invite' | 'resend'; inviteId?: string }
  | { kind: 'cancel'; invite: FamilyPortalInvite }
  | { kind: 'approve'; user: FamilyPortalUser }
  | { kind: 'revoke'; user: FamilyPortalUser }
  | { kind: 'age18'; user: FamilyPortalUser };

const FamilyPortalTab: React.FC<FamilyPortalTabProps> = ({ expanded, toggleExpanded, patientId }) => {
  const { showToast } = useToast();
  const canRead = useHasAnyPermission([FAMILY_PORTAL_READ]);
  const canWrite = useHasAnyPermission([FAMILY_PORTAL_WRITE]);
  const canApprove = useHasAnyPermission([FAMILY_PORTAL_APPROVE]);

  const [state, setState] = useState<FamilyPortalState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setState(await FamilyPortalService.getState(patientId));
    } catch (err) {
      setLoadError(familyPortalErrorMessage(err, 'Family portal details could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (expanded && canRead) void load();
  }, [expanded, canRead, load]);

  const latestInviteFor = useMemo(() => {
    const map = new Map<string, FamilyPortalInvite>();
    (state?.invites ?? []).forEach((invite) => {
      const prev = map.get(invite.patientGuardianId);
      if (!prev || new Date(invite.createdAt).getTime() > new Date(prev.createdAt).getTime()) {
        map.set(invite.patientGuardianId, invite);
      }
    });
    return map;
  }, [state]);

  const guardianById = useMemo(() => {
    const map = new Map<string, FamilyPortalGuardianRow>();
    (state?.guardians ?? []).forEach((g) => map.set(g.patientGuardianId, g));
    return map;
  }, [state]);

  const close = () => {
    if (!busy) setDialog({ kind: 'none' });
  };

  /** Run an action, show the outcome, reload. */
  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      showToast(success, { variant: 'success' });
      setDialog({ kind: 'none' });
      await load();
    } catch (err) {
      showToast(familyPortalErrorMessage(err), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (!canRead) return null;

  const pid = patientId ?? '';

  const renderGuardianActions = (guardian: FamilyPortalGuardianRow) => {
    const invite = latestInviteFor.get(guardian.patientGuardianId);
    const openInvite = invite && (invite.status === 'PENDING' || invite.status === 'LOCKED') ? invite : undefined;
    if (openInvite) {
      return (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={secondarySmall}
            disabled={!canWrite || busy}
            onClick={() => setDialog({ kind: 'invite', guardian, mode: 'resend', inviteId: openInvite.inviteId })}
          >
            Resend
          </button>
          {openInvite.status === 'PENDING' && (
            <button
              type="button"
              className={secondarySmall}
              disabled={!canWrite || busy}
              onClick={() => setDialog({ kind: 'cancel', invite: openInvite })}
            >
              Cancel invite
            </button>
          )}
        </div>
      );
    }
    const reasonText = !guardian.canInvite && guardian.cannotInviteReason ? CANNOT_INVITE_TEXT[guardian.cannotInviteReason] : null;
    if (guardian.cannotInviteReason === 'ALREADY_LINKED') {
      return <p className="text-sm text-slate-600">{reasonText}</p>;
    }
    const reasonId = `invite-reason-${guardian.patientGuardianId}`;
    return (
      <div className="flex flex-col items-start gap-1">
        <button
          type="button"
          className={primarySmall}
          disabled={!guardian.canInvite || !canWrite || busy}
          aria-describedby={reasonText ? reasonId : undefined}
          onClick={() => setDialog({ kind: 'invite', guardian, mode: 'invite' })}
        >
          Invite
        </button>
        {reasonText && (
          <p id={reasonId} className="text-xs text-slate-600">
            {reasonText}
          </p>
        )}
      </div>
    );
  };

  const renderUserActions = (user: FamilyPortalUser) => {
    const buttons: React.ReactNode[] = [];
    if (user.status === 'PENDING_APPROVAL' || user.status === 'ENDED') {
      buttons.push(
        <button
          key="approve"
          type="button"
          className={primarySmall}
          disabled={!canApprove || busy}
          onClick={() => setDialog({ kind: 'approve', user })}
        >
          {user.status === 'ENDED' ? 'Re-approve' : 'Approve'}
        </button>
      );
    }
    if (user.status === 'ACTIVE' && state?.age18ReviewDue && !user.age18ReviewedAt) {
      buttons.push(
        <button
          key="age18"
          type="button"
          className={secondarySmall}
          disabled={!canApprove || busy}
          onClick={() => setDialog({ kind: 'age18', user })}
        >
          Age-18 review
        </button>
      );
    }
    if (user.status === 'ACTIVE' || user.status === 'PENDING_APPROVAL') {
      buttons.push(
        <button
          key="revoke"
          type="button"
          className={dangerSmall}
          disabled={!canWrite || busy}
          onClick={() => setDialog({ kind: 'revoke', user })}
        >
          Revoke
        </button>
      );
    }
    return buttons.length > 0 ? <div className="flex flex-wrap gap-2">{buttons}</div> : null;
  };

  const approveDefaults =
    dialog.kind === 'approve' && dialog.user.patientGuardianId ? guardianById.get(dialog.user.patientGuardianId) : undefined;

  return (
    <section className="mb-4 rounded-lg bg-white shadow-md" aria-labelledby="family-portal-title">
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
      >
        <span className="flex items-center gap-2">
          <HeartHandshake size={20} className="text-slate-700" aria-hidden="true" />
          <h2 id="family-portal-title" className="text-lg font-semibold text-slate-800">
            Family Portal
          </h2>
        </span>
        {expanded ? <ChevronDown size={20} aria-hidden="true" /> : <ChevronRight size={20} aria-hidden="true" />}
      </button>
      {expanded && (
        <div className="space-y-5 px-4 pb-4">
          {loading && !state && <p role="status" className="text-sm text-slate-600">Loading family portal details...</p>}
          {loadError && (
            <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {loadError}
            </p>
          )}
          {state && (
            <>
              {!state.entitled && (
                <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                  Your plan doesn&apos;t include the family portal. Invites are turned off.
                </p>
              )}
              {(state.deceased || state.patientStatus === 'INACTIVE') && (
                <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                  {state.deceased
                    ? 'Portal access has ended for this patient. No invites or guardian emails can be sent.'
                    : 'This patient is discharged. Portal access has ended; re-approve guardians after readmission.'}
                </p>
              )}
              {state.age18ReviewDue && (
                <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                  This patient is 18 or older. Review each active guardian&apos;s access.
                </p>
              )}

              <div>
                <h3 id="family-portal-guardians" className="mb-2 text-base font-semibold text-slate-800">Legal guardians</h3>
                {state.guardians.length === 0 ? (
                  <p className="text-sm text-slate-600">
                    No legal guardians are recorded. Add them on the Demographics tab, with an email address, to invite them.
                  </p>
                ) : (
                  <ul aria-labelledby="family-portal-guardians" className="divide-y divide-slate-100 rounded-md border border-slate-200">
                    {state.guardians.map((guardian) => {
                      const invite = latestInviteFor.get(guardian.patientGuardianId);
                      return (
                        <li key={guardian.patientGuardianId} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800">{guardian.name}</p>
                            <p className="text-sm text-slate-600">
                              {guardian.relationship} · {legalAuthorityLabel(guardian.legalAuthority)}
                            </p>
                            <p className="break-all text-sm text-slate-600">{guardian.email || 'No email on file'}</p>
                            {invite && (
                              <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                <Pill status={invite.status} label={INVITE_STATUS_LABELS[invite.status] ?? invite.status} />
                                {invite.status === 'PENDING' && <span>Expires {fmtDate(invite.expiresAt)}</span>}
                                {invite.status === 'LOCKED' && <span>Locked after {invite.attempts} failed attempts. Resend to issue a new link.</span>}
                              </p>
                            )}
                          </div>
                          {renderGuardianActions(guardian)}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div>
                <h3 id="family-portal-users" className="mb-2 text-base font-semibold text-slate-800">Portal users</h3>
                {state.portalUsers.length === 0 ? (
                  <p className="text-sm text-slate-600">No one has accepted an invite yet.</p>
                ) : (
                  <ul aria-labelledby="family-portal-users" className="divide-y divide-slate-100 rounded-md border border-slate-200">
                    {state.portalUsers.map((user) => (
                      <li key={user.familyPortalUserId} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="flex flex-wrap items-center gap-2 font-medium text-slate-800">
                            {user.firstName} {user.lastName}
                            <Pill status={user.status} label={PORTAL_USER_STATUS_LABELS[user.status] ?? user.status} />
                          </p>
                          <p className="break-all text-sm text-slate-600">{user.email}</p>
                          <p className="text-sm text-slate-600">
                            {user.relationship || '—'} · {legalAuthorityLabel(user.legalAuthority)}
                          </p>
                          {user.approvedAt && user.status === 'ACTIVE' && (
                            <p className="text-xs text-slate-600">
                              Approved {fmtDate(user.approvedAt)}
                              {user.approvedBy ? ` by ${user.approvedBy}` : ''}
                            </p>
                          )}
                          {user.status === 'REVOKED' && user.revokeReason && (
                            <p className="text-xs text-slate-600">Reason: {user.revokeReason}</p>
                          )}
                          {user.status === 'ENDED' && user.endedReason && (
                            <p className="text-xs text-slate-600">Ended: {user.endedReason}</p>
                          )}
                          {user.age18ReviewedAt && (
                            <p className="text-xs text-slate-600">Age-18 review recorded {fmtDate(user.age18ReviewedAt)}</p>
                          )}
                        </div>
                        {renderUserActions(user)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <InviteDialog
        open={dialog.kind === 'invite'}
        guardian={dialog.kind === 'invite' ? dialog.guardian : null}
        mode={dialog.kind === 'invite' ? dialog.mode : 'invite'}
        busy={busy}
        onCancel={close}
        onConfirm={() => {
          if (dialog.kind !== 'invite') return;
          const { guardian, mode, inviteId } = dialog;
          void run(
            () =>
              mode === 'resend' && inviteId
                ? FamilyPortalService.resendInvite(pid, inviteId)
                : FamilyPortalService.invite(pid, guardian.patientGuardianId),
            mode === 'resend' ? 'Invite resent.' : 'Invite sent.'
          );
        }}
      />
      <ReasonDialog
        open={dialog.kind === 'revoke'}
        title="Revoke portal access"
        description="The guardian is signed out right away and can no longer see this patient."
        confirmLabel="Revoke access"
        destructive
        busy={busy}
        onCancel={close}
        onConfirm={(reason) => {
          if (dialog.kind !== 'revoke') return;
          const user = dialog.user;
          void run(() => FamilyPortalService.revoke(pid, user.familyPortalUserId, { reason }), 'Portal access revoked.');
        }}
      />
      <ApproveDialog
        open={dialog.kind === 'approve'}
        portalUser={dialog.kind === 'approve' ? dialog.user : null}
        defaultRelationship={approveDefaults?.relationship}
        defaultLegalAuthority={approveDefaults?.legalAuthority}
        busy={busy}
        onCancel={close}
        onConfirm={(relationship: string, legalAuthority: LegalAuthorityCode) => {
          if (dialog.kind !== 'approve') return;
          const user = dialog.user;
          void run(
            () => FamilyPortalService.approve(pid, user.familyPortalUserId, { relationship, legalAuthority }),
            'Guardian approved.'
          );
        }}
      />
      <Age18ReviewDialog
        open={dialog.kind === 'age18'}
        portalUser={dialog.kind === 'age18' ? dialog.user : null}
        busy={busy}
        onCancel={close}
        onConfirm={(decision, reason) => {
          if (dialog.kind !== 'age18') return;
          const user = dialog.user;
          void run(
            () => FamilyPortalService.age18Review(pid, user.familyPortalUserId, { decision, reason }),
            decision === 'revoke' ? 'Portal access revoked.' : 'Age-18 review recorded.'
          );
        }}
      />
      <ConfirmDialog
        open={dialog.kind === 'cancel'}
        title="Cancel this invite?"
        message="The link in the email stops working."
        confirmLabel="Cancel invite"
        cancelLabel="Keep invite"
        destructive
        busy={busy}
        onCancel={close}
        onConfirm={() => {
          if (dialog.kind !== 'cancel') return;
          const invite = dialog.invite;
          void run(() => FamilyPortalService.cancelInvite(pid, invite.inviteId), 'Invite cancelled.');
        }}
      />
    </section>
  );
};

export default FamilyPortalTab;
